import React, { useState } from 'react';
import { ArrowLeft, Heart, Sparkles, Flower2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ReligionType, ReligionIntent } from '@/hooks/useReligionSettings';

interface ReligionIntentGateProps {
  religion: ReligionType;
  onBack: () => void;
  onSelectIntent: (intent: ReligionIntent, waliNote?: string) => void;
}

const ReligionIntentGate: React.FC<ReligionIntentGateProps> = ({
  religion,
  onBack,
  onSelectIntent
}) => {
  const [selectedIntent, setSelectedIntent] = useState<ReligionIntent | null>(null);
  const [waliNote, setWaliNote] = useState('');

  const isMuslim = religion === 'muslim';

  const intents: { type: ReligionIntent; label: string; icon: React.ReactNode; description: string }[] = isMuslim
    ? [
        { 
          type: 'marriage', 
          label: 'Marriage', 
          icon: <Heart className="w-6 h-6" />,
          description: 'Looking for a life partner with shared faith and values'
        },
        { 
          type: 'exploring', 
          label: 'Exploring', 
          icon: <Sparkles className="w-6 h-6" />,
          description: 'Getting to know people in a respectful environment'
        },
      ]
    : [
        { 
          type: 'serious', 
          label: 'Serious', 
          icon: <Heart className="w-6 h-6" />,
          description: 'Looking for a meaningful, long-term connection'
        },
        { 
          type: 'exploring', 
          label: 'Exploring', 
          icon: <Sparkles className="w-6 h-6" />,
          description: 'Open to meeting new people who share my faith'
        },
      ];

  const handleContinue = () => {
    if (!selectedIntent) return;
    onSelectIntent(selectedIntent, isMuslim && selectedIntent === 'marriage' ? waliNote : undefined);
  };

  const getReligionIcon = () => {
    switch (religion) {
      case 'jewish': return '✡️';
      case 'christian': return '✝️';
      case 'muslim': return '☪️';
      case 'hindu': return '🕉️';
      default: return '🙏';
    }
  };

  const getReligionName = () => {
    switch (religion) {
      case 'jewish': return 'Jewish';
      case 'christian': return 'Christian';
      case 'muslim': return 'Muslim';
      case 'hindu': return 'Hindu';
      default: return 'Faith';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-rose-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="rounded-full"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2">
          <span className="text-2xl">{getReligionIcon()}</span>
          <h1 className="text-xl font-bold text-foreground">{getReligionName()}</h1>
        </div>
        <div className="w-10" />
      </div>

      {/* Question */}
      <div className="text-center mb-8">
        <Flower2 className="w-12 h-12 mx-auto mb-4 text-amber-500" />
        <h2 className="text-2xl font-bold text-foreground mb-2">
          What are you looking for?
        </h2>
        <p className="text-muted-foreground">
          {isMuslim ? 'This helps match you with compatible intentions' : 'Optional: helps find better matches'}
        </p>
        {isMuslim && (
          <span className="inline-block mt-2 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-3 py-1 rounded-full">
            Required for Muslim mode
          </span>
        )}
      </div>

      {/* Intent Options */}
      <div className="space-y-4 max-w-sm mx-auto mb-6">
        {intents.map((intent) => (
          <button
            key={intent.type}
            onClick={() => setSelectedIntent(intent.type)}
            className={`w-full p-4 rounded-2xl border-2 transition-all text-left flex items-start gap-4 ${
              selectedIntent === intent.type
                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                : 'border-muted-foreground/20 hover:border-amber-300'
            }`}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              selectedIntent === intent.type
                ? 'bg-amber-500 text-white'
                : 'bg-muted text-muted-foreground'
            }`}>
              {intent.icon}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground">{intent.label}</h3>
              <p className="text-sm text-muted-foreground">{intent.description}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Wali Note for Muslim Marriage Mode */}
      {isMuslim && selectedIntent === 'marriage' && (
        <div className="max-w-sm mx-auto mb-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <label className="block text-sm font-medium text-foreground mb-2">
            Wali/Family Note (Optional)
          </label>
          <Textarea
            value={waliNote}
            onChange={(e) => setWaliNote(e.target.value)}
            placeholder="Add a note about family involvement or wali preferences..."
            className="resize-none h-24 rounded-xl"
            maxLength={200}
          />
          <p className="text-xs text-muted-foreground mt-1">
            This will be visible to potential matches
          </p>
        </div>
      )}

      {/* Continue Button */}
      <div className="max-w-sm mx-auto">
        <Button
          onClick={handleContinue}
          disabled={!selectedIntent}
          className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue
        </Button>
      </div>

      {/* Disclaimer */}
      <p className="text-xs text-muted-foreground mt-6 text-center max-w-xs mx-auto">
        Your intent badge will be visible to others in this faith space
      </p>
    </div>
  );
};

export default ReligionIntentGate;
